import { Link } from 'react-router-dom';
import { Music2, Edit, Play, Pause, Zap, ArrowLeft, ListMusic, Heart } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Navigation } from '@/components/Navigation';
import { PlayerBar } from '@/components/PlayerBar';
import { TrackCard } from '@/components/TrackCard';
import { EditPlaylistDialog } from '@/components/EditPlaylistDialog';
import { ZapButton } from '@/components/ZapButton';
import { LikeButton } from '@/components/LikeButton';
import { CommentsSection } from '@/components/comments/CommentsSection';
import { usePlaylist } from '@/hooks/usePlaylist';
import { usePlaylistTracks } from '@/hooks/usePlaylistTracks';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useComments } from '@/hooks/useComments';
import { useLikes } from '@/hooks/useLikes';
import { genUserName } from '@/lib/genUserName';
import { useMusicPlayer } from '@/contexts/MusicPlayerContext';
import { useTrackZaps, formatSats } from '@/hooks/useTrackZaps';
import { useIsMobile } from '@/hooks/useIsMobile';
import { nip19 } from 'nostr-tools';

interface PlaylistPageProps {
  pubkey: string;
  playlistId: string;
}

export function PlaylistPage({ pubkey, playlistId }: PlaylistPageProps) {
  const { data: playlist, isLoading: isLoadingPlaylist, refetch } = usePlaylist(pubkey, playlistId);
  const { data: tracks, isLoading: isLoadingTracks } = usePlaylistTracks(playlist);
  const author = useAuthor(pubkey);
  const { user } = useCurrentUser();
  const { data: zaps } = useTrackZaps(
    playlist?.event.kind || 0,
    playlist?.pubkey || '',
    playlist?.id || ''
  );
  const { data: commentsData } = useComments(playlist?.event || new URL('about:blank'));
  const { data: likes } = useLikes(playlist?.event.id || '');
  const { currentTrack, isPlaying, playTrack, togglePlay } = useMusicPlayer();
  const isMobile = useIsMobile();

  const metadata = author.data?.metadata;
  const displayName = metadata?.name || genUserName(pubkey);
  const avatarUrl = metadata?.picture;

  const isOwnPlaylist = user?.pubkey === pubkey;

  // Calculate zap stats
  const totalZaps = zaps?.reduce((sum, zap) => sum + zap.amount, 0) || 0;
  const zapCount = zaps?.length || 0;

  // Get top zappers
  const topZappers = zaps?.slice(0, 10) || [];

  // Get comment count
  const commentCount = commentsData?.allComments?.length || 0;

  // Get like count
  const likeCount = likes?.length || 0;

  const handlePlayAll = () => {
    if (tracks && tracks.length > 0) {
      playTrack(tracks[0], tracks);
    }
  };

  const isCurrentPlaylist = tracks?.some((track) => track.event.id === currentTrack?.event.id);
  const isCurrentlyPlaying = isCurrentPlaylist && isPlaying;

  if (isLoadingPlaylist) {
    return <PlaylistPageSkeleton />;
  }

  if (!playlist) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card>
          <CardContent className="py-12 px-8 text-center">
            <Music2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Playlist not found</h2>
            <p className="text-muted-foreground mb-4">
              This playlist may have been deleted or the link is incorrect.
            </p>
            <Button onClick={() => (window.location.href = '/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const backgroundStyle = playlist.imageUrl
    ? { backgroundImage: `url(${playlist.imageUrl})` }
    : playlist.gradient
    ? { background: `linear-gradient(135deg, ${playlist.gradient.join(', ')})` }
    : { background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.6))' };

  return (
    <div className="min-h-screen bg-background md:pb-24">
      {/* Global Navigation */}
      <Navigation />

      {/* Hero Section with blurred background */}
      <div className="relative">
        {/* Blurred background */}
        <div
          className="absolute top-0 left-0 right-0 h-[250px] md:h-[300px] bg-cover bg-center blur-3xl opacity-45"
          style={backgroundStyle}
        />

        {/* Content */}
        <div className="relative mx-auto px-4 py-8 max-w-4xl 2xl:max-w-5xl">
          <div className="grid md:grid-cols-[300px,1fr] gap-8 max-w-lg md:max-w-none mx-auto">
            {/* Playlist Cover */}
            <div
              className="w-full aspect-square rounded-lg bg-cover bg-center shadow-2xl relative transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]"
              style={backgroundStyle}
            >
              {/* Show icon on gradient backgrounds (no image) */}
              {!playlist.imageUrl && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <ListMusic className="h-20 w-20 md:h-28 md:w-28 text-white drop-shadow-lg" />
                </div>
              )}
            </div>

            {/* Right Column: Playlist Info + Tabs */}
            <div className="space-y-8">
              {/* Playlist Info */}
              <div className="space-y-6">
                {/* Title and Creator */}
                <div>
                  <Badge variant="secondary" className="mb-3 gap-1.5">
                    <ListMusic className="h-3 w-3" />
                    Playlist
                  </Badge>
                  <h1 className="text-2xl md:text-4xl font-bold mb-1 md:mb-2">{playlist.title}</h1>
                  {playlist.description && (
                    <p className="text-muted-foreground whitespace-pre-wrap break-words mb-2 md:mb-4">
                      {playlist.description}
                    </p>
                  )}
                  <Link
                    to={`/${nip19.npubEncode(pubkey)}`}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {/* Desktop: Show avatar + name */}
                    <div className="hidden md:flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={avatarUrl} alt={displayName} />
                        <AvatarFallback>
                          {displayName.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-lg">{displayName}</span>
                    </div>
                    {/* Mobile: Show name only */}
                    <span className="md:hidden text-base">{displayName}</span>
                  </Link>
                </div>

                {/* Mobile Player Controls */}
                {isMobile && (
                  <div className="space-y-4">
                    {/* Play/Pause Button */}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={handlePlayAll}
                      disabled={!tracks || tracks.length === 0}
                      className="h-16 w-16 rounded-full border-0 bg-white text-black hover:bg-white/90 disabled:opacity-30 [&_svg]:!text-black"
                    >
                      {isCurrentlyPlaying ? (
                        <Pause className="h-7 w-7" />
                      ) : (
                        <Play className="h-7 w-7 ml-0.5" />
                      )}
                    </Button>
                  </div>
                )}

                {/* Tags */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    {playlist.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>

                  {/* Like and Zap Buttons - Mobile only */}
                  {isMobile && (
                    <div className="flex items-center gap-2">
                      <LikeButton
                        event={playlist.event}
                        size="icon"
                        className="h-12 w-12 bg-white/[0.06] hover:bg-white/[0.12]"
                      />
                      <ZapButton
                        event={playlist.event}
                        size="icon"
                        className="h-12 w-12 rounded-full bg-white/[0.06] hover:bg-white/[0.12]"
                      />
                    </div>
                  )}
                </div>

                {/* Desktop Play Button, Edit Button, Like Button, and Zap Stats */}
                <div className="hidden md:flex items-center gap-4">
                  {/* Play/Pause Button */}
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={handlePlayAll}
                    disabled={!tracks || tracks.length === 0}
                    className="h-14 w-14 rounded-full border-0 bg-white text-black hover:bg-white/90 disabled:opacity-30 [&_svg]:!text-black"
                  >
                    {isCurrentlyPlaying ? (
                      <Pause className="h-6 w-6" />
                    ) : (
                      <Play className="h-6 w-6 ml-0.5" />
                    )}
                  </Button>

                  {/* Edit Button - Only show if user owns the playlist */}
                  {isOwnPlaylist && (
                    <EditPlaylistDialog
                      playlist={playlist}
                      onSuccess={() => refetch()}
                      trigger={
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-14 w-14 rounded-full bg-white/[0.07] hover:bg-white/[0.12]"
                        >
                          <Edit className="h-5 w-5" />
                        </Button>
                      }
                    />
                  )}

                  {/* Like Button */}
                  <LikeButton
                    event={playlist.event}
                    size="icon"
                    className="h-14 w-14 bg-white/[0.07] hover:bg-white/[0.12]"
                  />

                  {/* Zap Stats */}
                  <div className="flex items-center gap-3">
                    {/* Zap Button */}
                    <ZapButton
                      event={playlist.event}
                      size="icon"
                      className="h-14 w-14 rounded-full bg-white/[0.07] hover:bg-white/[0.12]"
                    />

                    <div>
                      <p className="text-2xl font-bold">
                        {formatSats(totalZaps)} sats
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {zapCount} {zapCount === 1 ? 'zap' : 'zaps'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs Section */}
              <Tabs defaultValue="tracks" className="w-full">
                <TabsList className="h-8 glass border-0 p-1 rounded-full mb-6">
                  <TabsTrigger value="tracks" className="text-xs px-3 py-1 rounded-full data-[state=active]:glass-strong gap-2">
                    <span>Tracks</span>
                    <Badge variant="secondary" className="h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                      {playlist.trackRefs.length}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger value="comments" className="text-xs px-3 py-1 rounded-full data-[state=active]:glass-strong gap-2">
                    <span>Comments</span>
                    <Badge variant="secondary" className="h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                      {commentCount}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger value="reactions" className="text-xs px-3 py-1 rounded-full data-[state=active]:glass-strong gap-2">
                    <span>Reactions</span>
                    <Badge variant="secondary" className="h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                      {likeCount}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger value="zappers" className="text-xs px-3 py-1 rounded-full data-[state=active]:glass-strong">
                    Top Zappers
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="tracks" className="mt-0">
                  {isLoadingTracks ? (
                    <div className="space-y-4">
                      {[...Array(3)].map((_, i) => (
                        <TrackCardSkeleton key={i} />
                      ))}
                    </div>
                  ) : tracks && tracks.length > 0 ? (
                    <div className="space-y-4">
                      {tracks.map((track) => (
                        <TrackCard key={`${track.pubkey}:${track.id}`} track={track} playlist={tracks} />
                      ))}
                    </div>
                  ) : (
                    <Card className="glass border-0">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground text-center py-8">
                          {isOwnPlaylist ? 'Add some tracks to your playlist' : 'This playlist is empty'}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="comments" className="mt-0">
                  <CommentsSection
                    root={playlist.event}
                    title=""
                    emptyStateMessage="No comments yet"
                    emptyStateSubtitle="Share your thoughts about this playlist!"
                  />
                </TabsContent>

                <TabsContent value="reactions" className="mt-0">
                  {likeCount === 0 ? (
                    <Card className="glass border-0">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground text-center py-8">
                          No reactions yet. Be the first!
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="glass border-0">
                      <CardContent className="p-4 space-y-1">
                        {likes?.map((like) => (
                          <ReactionCard
                            key={like.id}
                            pubkey={like.pubkey}
                          />
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="zappers" className="mt-0">
                  {topZappers.length === 0 ? (
                    <Card className="glass border-0">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground text-center py-8">
                          No zaps yet. Be the first!
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div>
                      {topZappers.map((zap, i) => (
                        <ZapperCard
                          key={i}
                          pubkey={zap.zapper}
                          amount={zap.amount}
                          comment={zap.comment}
                          rank={i + 1}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Player Bar - Hidden on mobile when on PlaylistPage */}
      {!isMobile && <PlayerBar />}
    </div>
  );
}

function ZapperCard({
  pubkey,
  amount,
  comment,
  rank,
}: {
  pubkey: string;
  amount: number;
  comment?: string;
  rank: number;
}) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name || genUserName(pubkey);
  const avatarUrl = metadata?.picture;

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/[0.05] transition-colors">
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="flex items-center justify-center h-8 w-8 rounded-full bg-white/[0.30] text-sm font-bold">
          {rank}
        </div>
        <Link to={`/${nip19.npubEncode(pubkey)}`}>
          <Avatar className="h-10 w-10 hover:opacity-80 transition-opacity">
            <AvatarImage src={avatarUrl} alt={displayName} />
            <AvatarFallback>{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
        </Link>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <Link
            to={`/${nip19.npubEncode(pubkey)}`}
            className="text-sm font-medium truncate hover:underline"
          >
            {displayName}
          </Link>
          <Badge variant="secondary" className="gap-1 flex-shrink-0">
            <Zap className="h-3 w-3" />
            {formatSats(amount)}
          </Badge>
        </div>
        {comment && (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
            {comment}
          </p>
        )}
      </div>
    </div>
  );
}

function ReactionCard({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name || genUserName(pubkey);
  const avatarUrl = metadata?.picture;

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.05] transition-colors cursor-pointer">
      <Link to={`/${nip19.npubEncode(pubkey)}`}>
        <Avatar className="h-8 w-8 hover:opacity-80 transition-opacity">
          <AvatarImage src={avatarUrl} alt={displayName} />
          <AvatarFallback>{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
      </Link>
      <div className="flex-1 min-w-0">
        <Link
          to={`/${nip19.npubEncode(pubkey)}`}
          className="text-sm font-medium truncate hover:underline"
        >
          {displayName}
        </Link>
      </div>
      <Badge variant="secondary" className="gap-1 flex-shrink-0 bg-rose-500/10 text-rose-500 hover:bg-rose-500/10">
        <Heart className="h-3 w-3 fill-current" />
      </Badge>
    </div>
  );
}

function TrackCardSkeleton() {
  return (
    <Card className="glass border-0 overflow-hidden">
      <div className="flex items-center gap-4 p-2">
        <Skeleton className="w-16 h-16 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2 min-w-0">
          <Skeleton className="h-5 w-3/4" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-10 w-10 rounded-full" />
        </div>
      </div>
    </Card>
  );
}

function PlaylistPageSkeleton() {
  return (
    <div className="min-h-screen bg-background md:pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/[0.11] backdrop-blur-md">
        <div className="w-full px-4 py-2 md:px-6 md:py-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-24" />
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-32" />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="relative">
        <div className="relative mx-auto px-4 py-8 max-w-4xl 2xl:max-w-5xl">
          <div className="grid md:grid-cols-[300px,1fr] gap-8 max-w-lg md:max-w-none mx-auto">
            <Skeleton className="w-full aspect-square rounded-lg" />
            <div className="space-y-8">
              {/* Playlist Info */}
              <div className="space-y-6">
                <div>
                  <Skeleton className="h-6 w-20 rounded-full mb-3" />
                  <Skeleton className="h-10 w-64 mb-2" />
                  <Skeleton className="h-4 w-full mb-1" />
                  <Skeleton className="h-4 w-3/4 mb-4" />
                  <Skeleton className="h-6 w-32" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-6 w-20 rounded-full" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                </div>
                <div className="hidden md:flex items-center gap-4">
                  <Skeleton className="h-14 w-14 rounded-full" />
                  <Skeleton className="h-14 w-14 rounded-full" />
                  <div className="space-y-1">
                    <Skeleton className="h-8 w-32" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="space-y-4">
                <Skeleton className="h-8 w-64 rounded-full" />
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <TrackCardSkeleton key={i} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
